/**
 * 產品圖片「下載到本機」的**執行**邏輯（需求 R152）。
 *
 * 沿革：
 *   需求 R142（PM 2026-08-29 拍板 Q1=A「只做產品詳情頁」）把這段
 *   fetch → blob → a.download 與三條退場路徑寫在 ProductDetailPage 的
 *   handleDownloadImage 裡，直接讀該頁的 form.productImage / form.productName。
 *
 *   需求 R152（PM 2026-09-01 留言）要求「報價單-詳情頁（已審核通過鎖住的
 *   頁面）、產品管理、型錄管理，滑鼠移到產品縮圖時出現下載按鈕，**下載結果與產品詳細頁
 *   執行結果相同**」——「相同」就是本檔存在的理由：把那段邏輯原封不動搬出來成為單一來源，
 *   ProductDetailPage 與各縮圖共用同一份，日後改一次就四處一致，不會再長出第二份實作。
 *
 * 搬移原則（需求 R152 AC8）：alert 文案、判斷順序、a.download 流程**逐字不變**，
 * 只把 form.productImage / form.productName 換成參數 src / productName。
 *
 * 為什麼另開一支檔案而不寫進 productImageDownload.js：
 *   那支是**純字串運算、刻意零 import**，好讓 fixture 用 babel 直接載入 Node 驗算檔名規則
 *   （見該檔檔頭與 需求 R140 的教訓）。本檔會碰 fetch / window / document，且必須 import
 *   那支的 buildImageFileName——兩者混在一起會把「可在 Node 直接驗算」這個性質弄丟。
 *   本檔只 import 同目錄的 productImageDownload.js 一支，載入時不執行任何 DOM／網路行為。
 */

import { buildImageFileName } from './productImageDownload';

/**
 * 下載一張產品圖片到本機。
 *
 * 為什麼不能用 <a href={src} download="豬肉紙.jpg">：
 *   productImage 不保證是站內相對路徑 —— 舊資料可能是外部絕對網址（見
 *   quotePdfExport.js:137 / catalogPdfExport.js:595 的 startsWith('http') 分支）。
 *   HTML 的 download 屬性受同源限制，跨網域時瀏覽器會**忽略**它，變成直接在分頁
 *   把圖開起來、檔名也套不上去。因此一律走 fetch → blob →
 *   URL.createObjectURL → a.download，與站內其他匯出一致（ProductsPage.js:801-806）。
 *
 * 三條退場路徑刻意分開，不要合成一個 catch：
 *   1) fetch 本身 throw（TypeError）＝ 連線層／CORS 失敗，圖其實存在但讀不到位元組
 *      → 需求 R142 Q6=A：改用新分頁開圖並提示右鍵另存。
 *   2) 回應 !res.ok（例如同源 404，檔案被刪）＝ 圖真的不在了
 *      → 開新分頁只會給使用者看一頁 404，所以只出可讀錯誤訊息，不開分頁。
 *   3) blob.size === 0 ＝ 存檔壞掉
 *      → 直接擋下，避免下載出 0 byte 壞檔。
 *
 * @param {string} src 產品圖片網址（Product.productImage，站內相對路徑或 http 絕對網址）
 * @param {string} productName 產品名稱（決定下載檔名，規則見 productImageDownload.js）
 * @returns {Promise<void>}
 */
export async function downloadProductImage(src, productName) {
  // 呼叫端都已保證按鈕只在有圖時渲染；這裡是第二道防線（需求 R152 AC12）。
  if (!src) {
    alert('此產品尚無圖片可下載。');
    return;
  }

  let res;
  try {
    res = await fetch(src);
  } catch (netErr) {
    // Q6=A：跨網域舊圖 fetch 不到 → 新分頁開啟＋提示右鍵另存。
    console.error('下載圖片失敗（連線／跨網域）', netErr);
    const opened = window.open(src, '_blank', 'noopener,noreferrer');
    alert(opened
      ? '無法直接下載這張圖片（外部網址不允許跨網域讀取）。已在新分頁開啟，請在圖片上按右鍵選「另存圖片」。'
      : '無法直接下載這張圖片（外部網址不允許跨網域讀取），且瀏覽器擋下了新分頁。請允許彈出視窗後再試一次。');
    return;
  }

  if (!res.ok) {
    alert(`圖片下載失敗（HTTP ${res.status}），檔案可能已不存在，請重新上傳圖片。`);
    return;
  }

  try {
    const blob = await res.blob();
    if (!blob || blob.size === 0) {
      alert('圖片下載失敗：伺服器回傳的檔案是空的，請重新上傳圖片。');
      return;
    }
    // Q4=A 副檔名依實際圖片、Q5=A 非法字元換底線／空名用 product-image（見 utils）。
    const filename = buildImageFileName(productName, src, blob.type);
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(objectUrl);
  } catch (err) {
    console.error('下載圖片失敗', err);
    alert('圖片下載失敗，請稍後再試。');
  }
}

export default downloadProductImage;
